import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getTrialCardQuote, getTrialUpgradePrompt, recordTrialUpgradePromptOp, TrialCardQuote, TrialUpgradePromptResponse } from '@extension/wa-communication-layer/api/api-calls';
import { TrialCardConsent } from '@common/components/trial-card-return/trial-card-consent';
import { shouldShowTrialUpgradePrompt, trialUpgradeCheckoutUrl } from './trial-upgrade-prompt-logic';

type ShowableResponse = TrialUpgradePromptResponse & {
  type: NonNullable<TrialUpgradePromptResponse['type']>;
  plan: NonNullable<TrialUpgradePromptResponse['plan']>;
  copy: NonNullable<TrialUpgradePromptResponse['copy']>;
};

/**
 * #1226 slice A: one dismissible in-trial upgrade popup. All copy comes from the server
 * (single source of truth in the backend). Both buttons are the same size and style, so
 * "Not now" carries equal weight to the primary action. Renders nothing unless the server
 * says show:true.
 */
export const TrialUpgradePrompt: React.FC = () => {
  const [prompt, setPrompt] = useState<ShowableResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [consentQuote, setConsentQuote] = useState<TrialCardQuote | null>(null);
  // The card-add quote, read once when the popup appears so the click handler can act synchronously.
  const [prefetchedQuote, setPrefetchedQuote] = useState<TrialCardQuote | null>(null);
  const fetchedRef = useRef(false);
  const shownRecordedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getTrialUpgradePrompt()
      .then((res) => {
        if (shouldShowTrialUpgradePrompt(res)) {
          setPrompt(res);
          setOpen(true);
        }
      })
      .catch(() => { /* a failed decision check never blocks the app: no popup */ });
  }, []);

  // Record `shown` once, when the popup actually appears (server validates the arm).
  useEffect(() => {
    if (!open || !prompt || shownRecordedRef.current) return;
    shownRecordedRef.current = true;
    recordTrialUpgradePromptOp('shown', prompt.type).catch(() => {});
  }, [open, prompt]);

  // One server decision: the popup's own plan is what the quote prices, so both screens agree.
  useEffect(() => {
    if (!open || !prompt) return;
    getTrialCardQuote(prompt.plan)
      .then((quote) => { if (quote.kind === 'ok' && 'copy' in quote) setPrefetchedQuote(quote); })
      .catch(() => { /* no quote = the normal checkout */ });
  }, [open, prompt]);

  if (!prompt) return null;

  const close = (): void => setOpen(false);

  const onPrimary = (): void => {
    recordTrialUpgradePromptOp('clicked', prompt.type).catch(() => {});
    close();
    // #1229: in the card-add arm the CTA opens the consent screen first; for everyone else (flag off, other arm,
    // no quote) it is the normal checkout, exactly as before. Decided from the prefetched quote, so the
    // checkout tab opens synchronously inside this click and is not popup-blocked.
    if (prefetchedQuote !== null) setConsentQuote(prefetchedQuote);
    else openCheckout();
  };

  const openCheckout = (): void => {
    window.open(trialUpgradeCheckoutUrl(prompt.plan), '_blank', 'noopener');
  };

  const onNotNow = (): void => {
    recordTrialUpgradePromptOp('dismissed', prompt.type).catch(() => {});
    close();
  };

  // Equal weight: identical variant, size and class for both buttons.
  const equalButtonClass = 'min-w-[9rem] flex-1 h-auto whitespace-normal py-2';

  return (
    <>
    <Dialog open={open} onOpenChange={(next) => { if (!next) onNotNow(); }}>
      <DialogContent className="max-w-md" data-testid="trial-upgrade-prompt">
        <DialogHeader>
          <DialogTitle>{prompt.copy.heading}</DialogTitle>
          <DialogDescription>{prompt.copy.body}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{prompt.copy.footer}</p>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className={equalButtonClass} onClick={onNotNow}>
            {prompt.copy.notNow}
          </Button>
          <Button variant="outline" className={equalButtonClass} onClick={onPrimary}>
            {prompt.copy.button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {consentQuote !== null && (
      <TrialCardConsent
        quote={consentQuote}
        notNowLabel={prompt.copy.notNow}
        onCancel={() => setConsentQuote(null)}
        fallbackUrl={trialUpgradeCheckoutUrl(prompt.plan)}
        onFallback={() => setConsentQuote(null)}
      />
    )}
    </>
  );
};
